import React from 'react';
import PropTypes from 'prop-types';
import navigationService from '../../services/NavigationService';
import { t } from '../../services/i18nService';
import styles from './options.scss';
import backgroundAudioService from '../../services/BackgroundAudioService';
import storeManager from '../../store/StoreManager';
import sessionActions from '../../actions/SessionActions';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../services/a11yIIBScreenReaderService';

class OptionsButton extends React.Component {
    static propTypes = {
        buttonProps: PropTypes.object,
    };

    handleNavigateToOptions() {
        storeManager.dispatch(sessionActions.toggleInvigilatorAuthorized(false));
        navigationService.navigateToOptions();
    }

    render() {
        return (
            <button
                {...(this.props.buttonProps || {})}
                className={styles.optionsButton}
                onClick={this.handleNavigateToOptions}
            >
                <span className="sr-only">{t('options.backToOptionsList')}</span>
                <i className="fa fa-chevron-left" aria-hidden="true" /> {t('options.optionsHeader')}
            </button>
        );
    }
}

class BackToDashboardButton extends React.Component {
    static propTypes = {
        buttonProps: PropTypes.object,
    };

    handleNavigateToDashboard() {
        navigationService.navigateToDashboardTicket();
    }

    render() {
        return (
            <button
                {...(this.props.buttonProps || {})}
                className={styles.optionsButton}
                onClick={this.handleNavigateToDashboard}
            >
                <span className="sr-only">{t('options.backToDashboard')}</span>
                <i className="fa fa-chevron-left" aria-hidden="true" /> {t('options.dashboardLabel')}
            </button>
        );
    }
}

class InvigilatorOptionsButton extends React.Component {
    static propTypes = {
        buttonProps: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.keyPress = (event) => {
            if (event.keyCode === 8) {
                event.preventDefault();
                event.stopPropagation();
            }
        };
        // Storing function locally in the class because .bind() returns
        // a new function when bound directly in the eventListener
        this.keyPressHandler = this.keyPress.bind(this);
        document.addEventListener('keydown', this.keyPressHandler);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.keyPressHandler);
    }

    handleNavigateToInvigilatorOptions() {
        backgroundAudioService.resume();
        navigationService.navigateToInvigilatorOptions();
    }

    render() {
        return (
            <button
                {...(this.props.buttonProps || {})}
                className={styles.optionsButton}
                onClick={this.handleNavigateToInvigilatorOptions}
            >
                <span className="sr-only">{t('options.backToOptionsList')}</span>
                <i className="fa fa-chevron-left" aria-hidden="true" /> {t('options.optionsHeader')}
            </button>
        );
    }
}

class ExitButton extends React.Component {
    static propTypes = {
        buttonProps: PropTypes.object,
    };

    render() {
        return (
            <button
                {...(this.props.buttonProps || {})}
                className={styles.exitButton}
                id="exitOptionsMenuButton"
                onClick={navigationService.exitFromOptions}
            >
                <span className="sr-only">{t('options.btnLabelExitFromOptions')}</span>
                <i className="fa fa-close fa-3" aria-hidden="true" />
            </button>
        );
    }
}

class OptionsTopBar extends React.Component {
    static propTypes = {
        title: PropTypes.string.isRequired,
        showExitButton: PropTypes.bool,
        optionsButtonProps: PropTypes.object,
        backButtonProps: PropTypes.object,
        invigilatorOptionsButtonProps: PropTypes.object,
        exitButtonProps: PropTypes.object,
        showOptionsButton(props) {
            if (props.showOptionsButton && props.showBackToDashboardButton) {
                return new Error(
                    'Only one of components (OptionsButton or BackToDashboardButton) can be used in the "OptionsTopBar" at the same time'
                );
            }
            return false;
        },
        showInvigilatorOptionsButton: PropTypes.bool,
        showBackToDashboardButton(props) {
            if (props.showOptionsButton && props.showBackToDashboardButton) {
                return new Error(
                    'Only one of components (OptionsButton or BackToDashboardButton) can be used in the "OptionsTopBar" at the same time'
                );
            }
            return false;
        },
    };

    componentDidMount() {
        this.focusHeadingIfNeeded();
    }

    componentDidUpdate() {
        this.focusHeadingIfNeeded();
    }

    /**
     * Focuses the heading when navigating to options pages for screen reader announcement.
     * v1: Programmatically focuses the h1 for context announcement.
     * v2: Skips programmatic focus, relying on native browser focus management with enhanced
     * ARIA attributes (role="banner", etc.) applied via getA11yAttributesV2().
     */
    focusHeadingIfNeeded() {
        const isV2Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
            A11yForIIBScreenReaderIteration.v2
        );
        if (!isV2Supported && this.h1) {
            this.h1.focus();
        }
    }

    render() {
        return (
            <div className={styles.optionsTopBar} role="banner">
                {
                    this.props.showOptionsButton && !this.props.showBackToDashboardButton
                        ? <OptionsButton buttonProps={this.props.optionsButtonProps} />
                        : null
                }

                {
                    this.props.showBackToDashboardButton && !this.props.showOptionsButton
                        ? <BackToDashboardButton buttonProps={this.props.backButtonProps} />
                        : null
                }

                {
                    this.props.showInvigilatorOptionsButton
                        ? <InvigilatorOptionsButton buttonProps={this.props.invigilatorOptionsButtonProps} />
                        : null
                }

                <div className={styles.title}>
                    <h1 tabIndex="-1" ref={(h1) => { this.h1 = h1; }}>{this.props.title}</h1>
                </div>

                {
                    this.props.showExitButton === false ?
                        null
                        :
                        <ExitButton buttonProps={this.props.exitButtonProps} />

                }

            </div>
        );
    }
}

export default OptionsTopBar;
